import { jwtDecode } from 'jwt-decode'; // Correct import for jwt-decode
import { buildAuthUrl, fetchWithRefreshToken } from '../services/HttpService';
// import { get } from 'http';

export const parseJwt = (token) => {
    try {
        // Using jwt-decode library
        const decoded = jwtDecode(token);   
        // console.log('Decoded JWT:', decoded);
        return decoded;
    } catch (error) {
        console.error('Invalid JWT:', error);
        return null;
    }
};

export const cacheAccessTokenProps = () => {
    setTenantName(accessTokenTenantName());
    setTenantDisplayname(accessTokenTenantDisplayname());
    setUserName(accessTokenUserName());  

    // Whenever you update localStorage:
    window.dispatchEvent(new Event('userNameChanged'));
}

export const clearCachedAccessTokenProps = () => {

    setTenantName(null);
    setUserName(null);

    setAccessToken(null);
    setRefreshToken(null);
    setExpirationAccessToken(null);
    setExpirationRefreshToken(null);
    clearAvailableTenants();
    setTenantSelectionPending(false);
}


export const getAccessToken = () => {
    return localStorage.getItem('BADDOG-accessToken');
};
export const getRefreshToken = () => {
    return localStorage.getItem('BADDOG-refreshToken');
};

export const getUserName = () => {
    return localStorage.getItem('userName');
};

export const getTenantName = () => {
    const value = localStorage.getItem('tenantName');
    // Handle null, undefined, and the string "null" (from old code)
    if (!value || value === 'null') {
        return "ORGANIZATION (Tenant) NOT CONFIGURED";
    }
    return value;
};

export const getTenantDisplayname = () => {
    const value = localStorage.getItem('tenantDisplayname');
    // Handle null, undefined, and the string "null" (from old code)
    if (!value || value === 'null') {
        return "ORGANIZATION (Tenant) Displayname NOT CONFIGURED";
    }
    return value;
};

export const getExpirationAccessToken = () => {
    return localStorage.getItem('expirationAccessToken');
};
export const getExpirationRefreshToken = () => {
    return localStorage.getItem('expirationRefreshToken');
};



export const setAccessToken = (tokenValue) => {
    if (tokenValue === null || tokenValue === undefined) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('BADDOG-accessToken');
    } else {
        localStorage.setItem('authToken', tokenValue);
        localStorage.setItem('BADDOG-accessToken', tokenValue);
        cacheAccessTokenProps();
    }
};
export const setRefreshToken = (tokenValue) => {
    if (tokenValue === null || tokenValue === undefined) {
        localStorage.removeItem('BADDOG-refreshToken');
    } else {
        localStorage.setItem('BADDOG-refreshToken', tokenValue);
    }
};


export const setUserName = (u) => {
    if (u === null || u === undefined) {
        localStorage.removeItem('userName');
    } else {
        localStorage.setItem('userName', u);
    }
};

export const setTenantName = (t) => {
    if (t === null || t === undefined) {
        localStorage.removeItem('tenantName');
    } else {
        localStorage.setItem('tenantName', t);
    }
};

export const setTenantDisplayname = (t) => {
    if (t === null || t === undefined) {
        localStorage.removeItem('tenantDisplayname');
    } else {
        localStorage.setItem('tenantDisplayname', t);
    }
};

export const setExpirationAccessToken = (u) => {
    if (u === null || u === undefined) {
        localStorage.removeItem('expirationAccessToken');
    } else {
        localStorage.setItem('expirationAccessToken', u);
    }
};
export const setExpirationRefreshToken = (t) => {
    if (t === null || t === undefined) {
        localStorage.removeItem('expirationRefreshToken');
    } else {
        localStorage.setItem('expirationRefreshToken', t);
    }
};

// Available tenants - list of { tenantname } objects the user has access to
export const getAvailableTenants = () => {
    try {
        const value = localStorage.getItem('TELESCOPE-availableTenants');
        return value ? JSON.parse(value) : [];
    } catch {
        return [];
    }
};
export const setAvailableTenants = (tenants) => {
    if (!tenants || !Array.isArray(tenants)) {
        localStorage.removeItem('TELESCOPE-availableTenants');
    } else {
        localStorage.setItem('TELESCOPE-availableTenants', JSON.stringify(tenants));
    }
};
export const clearAvailableTenants = () => {
    localStorage.removeItem('TELESCOPE-availableTenants');
};

// Signals that the user has multiple tenants and must choose one before proceeding
export const isTenantSelectionPending = () => {
    return localStorage.getItem('TELESCOPE-tenantSelectionPending') === 'true';
};
export const setTenantSelectionPending = (pending) => {
    if (pending) {
        localStorage.setItem('TELESCOPE-tenantSelectionPending', 'true');
    } else {
        localStorage.removeItem('TELESCOPE-tenantSelectionPending');
    }
};


export const accessTokenExp = () => {
    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.exp 
        : null;
};
export const refreshTokenExp = () => {
    const token = getRefreshToken();
    
    return token 
        ? parseJwt(token)?.exp 
        : null;
};

export const accessTokenAuthorities = () => {
    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.authorities 
        : null;
};

const SUBJECT_WILDCARDS = ['_ANY', '_ANY_'];

/**
 * Session tenant for authority checks (JWT claim, then cached tenant name).
 * @returns {string|null}
 */
export function resolveSessionTenantName() {
    const fromToken = accessTokenTenantName();
    if (fromToken) return fromToken;
    const cached = getTenantName();
    if (cached && cached !== 'ORGANIZATION (Tenant) NOT CONFIGURED') {
        return cached;
    }
    return null;
}

/**
 * Granted subject satisfies required subject (Bad Dog verifyAuthority subject rules).
 * @param {string} grantedSubject
 * @param {string} requiredSubject
 */
export function subjectAuthorityMatches(grantedSubject, requiredSubject) {
    if (SUBJECT_WILDCARDS.includes(grantedSubject)) return true;
    return grantedSubject === requiredSubject;
}

/**
 * Granted action satisfies required action (_ALL is a wildcard).
 * @param {string} grantedAction
 * @param {string} requiredAction
 */
export function actionAuthorityMatches(grantedAction, requiredAction) {
    if (grantedAction === '_ALL') return true;
    return grantedAction === requiredAction;
}

/**
 * Tenant dimension: anyTenant bypasses session tenant; else authority.tenantname must match.
 * @param {Object} authority
 * @param {string|null} sessionTenant
 * @param {boolean} [requireAnyTenant]
 */
export function tenantAuthorityMatches(authority, sessionTenant, requireAnyTenant = false) {
    if (requireAnyTenant && !isAnyTenantAuthority(authority)) return false;
    if (isAnyTenantAuthority(authority)) return true;
    if (!sessionTenant) return false;
    return authority.tenantname === sessionTenant;
}

/**
 * Evaluate authority requirements against a JWT authorities list (testable without token I/O).
 * @param {Array|null} authorities
 * @param {Object} requirement
 * @param {string|null} sessionTenant
 * @returns {boolean}
 */
export function matchJwtAuthorities(authorities, requirement, sessionTenant = null) {
    if (!requirement?.scope || !requirement?.subject || !requirement?.action) {
        return false;
    }
    if (!authorities || !Array.isArray(authorities)) return false;

    const tenant = requirement.tenantname ?? sessionTenant;

    return authorities.some((entry) => {
        const authority = entry?.authority;
        if (!authority) return false;
        if (authority.scopename !== requirement.scope) return false;
        if (!subjectAuthorityMatches(authority.subjectname, requirement.subject)) return false;
        if (!actionAuthorityMatches(authority.actionname, requirement.action)) return false;
        return tenantAuthorityMatches(authority, tenant, requirement.requireAnyTenant === true);
    });
}

/**
 * Check JWT authorities against scope + subject + action + tenant (mirrors Bad Dog verifyAuthority).
 * @param {Object} requirement
 * @param {string} requirement.scope - e.g. "Security", "DB Admin", "Business"
 * @param {string} requirement.subject - e.g. "Tenant", "AppUser", "_ANY_"
 * @param {string} requirement.action - e.g. "READ", "CREATE", "DELETE", "_ALL"
 * @param {boolean} [requirement.requireAnyTenant] - when true, authority.anyTenant must be true
 * @param {string} [requirement.tenantname] - explicit tenant; defaults to session tenant
 * @returns {boolean}
 */
export function hasJwtAuthority(requirement) {
    const sessionTenant = requirement.tenantname ?? resolveSessionTenantName();
    return matchJwtAuthorities(accessTokenAuthorities(), requirement, sessionTenant);
}

/**
 * True when the JWT satisfies any one of the authority requirements (OR gate).
 * @param {Object[]} requirements
 * @returns {boolean}
 */
export function hasAnyJwtAuthority(requirements = []) {
    if (!Array.isArray(requirements) || requirements.length === 0) return false;
    const sessionTenant = resolveSessionTenantName();
    const authorities = accessTokenAuthorities();
    return requirements.some((req) =>
        matchJwtAuthorities(authorities, req, req.tenantname ?? sessionTenant)
    );
}

/**
 * @deprecated Use hasJwtAuthority — rolename is not an authorization dimension in Telescope.
 */
export const hasJwtRole = (requiredScope, requiredRole) => {
    const authorities = accessTokenAuthorities();
    if (!authorities || !Array.isArray(authorities)) return false;
    return authorities.some(
        (a) =>
            a?.authority?.scopename === requiredScope &&
            a?.authority?.rolename === requiredRole
    );
};

/**
 * True when a JWT authority is global (any-tenant), not tenant-scoped.
 * Only explicit true counts — tenant-scoped roles have anyTenant: false.
 */
export function isAnyTenantAuthority(authority) {
    if (!authority) return false;
    const value = authority.anyTenant;
    if (value === true) return true;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
}

/**
 * @deprecated Use hasJwtAuthority with requireAnyTenant — rolename is not an authorization dimension.
 */
export const hasJwtRoleAnyTenant = (requiredScope, requiredRole) => {
    const authorities = accessTokenAuthorities();
    if (!authorities || !Array.isArray(authorities)) return false;
    return authorities.some(
        (a) =>
            a?.authority?.scopename === requiredScope &&
            a?.authority?.rolename === requiredRole &&
            isAnyTenantAuthority(a?.authority)
    );
};

export const accessTokenTenantName = () => {

    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.tenantname ?? null
        : null;
};

export const accessTokenTenantDisplayname = () => {

    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.tenantDisplayname ?? null
        : null;

};

export const accessTokenSub = () => {
    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.sub
        : null;
};

export const accessTokenPreferredUsername = () => {
    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.preferred_username
        : null;
};

export const accessTokenUserName = () => {
    return accessTokenPreferredUsername() || "";
    // const token = getAccessToken();

    // const sub = accessTokenSub();
    // const tenantName = accessTokenTenantName();

    // if (sub && tenantName) {
    //     // If the sub contains a '.', assume it's in the format 'tenantname.username'
    //     if (sub.includes('.')) {
    //         return sub.substring(sub.indexOf('.') + 1);
    //     } else {
    //         return sub;
    //     }
    // }
}

export const accessTokenExpiration = () => {
    const tokenExp = accessTokenExp();
    return new Date( tokenExp * 1000);
};
export const refreshTokenExpiration = () => {
    const tokenExp = refreshTokenExp();
    return new Date( tokenExp * 1000);
};

export const accessTokenIsExpired = () => {
    const now = new Date().getTime();
    const exp = accessTokenExpiration();
    return now > exp;
};
export const refreshTokenIsExpired = () => {
    const now = new Date().getTime();
    const exp = refreshTokenExpiration();
    return now > exp;
};

export const accessTokenIsFresh = () => {
    const token = getAccessToken();
    
    return token 
        ? parseJwt(token)?.fresh 
        : false;
};

// var timeDifference = exp - now;
// var differenceDate = new Date(timeDifference * 1000);
// var diffHours = differenceDate.getUTCHours();
// var diffMinutes = differenceDate.getUTCMinutes();
// var diffSeconds = differenceDate.getUTCSeconds();
// var readableDifference = diffHours + ':' + diffMinutes + ':' + diffSeconds;

export default parseJwt;